// Completes Identity Platform email-link sign-in, then exchanges the ID token
// for our rrid_session cookie and drops the Firebase client session.
import { useEffect, useRef, useState } from "react";
import { isSignInWithEmailLink, signInWithEmailLink, signOut } from "firebase/auth";
import { unauthorizedLoginPath, type LoginRejectReason } from "@rrid/shared-types";
import { useAuth } from "../lib/auth.js";
import { useFeatures } from "../hooks/useFeatures.js";
import { EMAIL_FOR_SIGN_IN_KEY, getFirebaseAuth } from "../lib/firebaseAuth.js";
import { describeEmailLinkCompleteError, persistLoginError } from "../lib/loginError.js";
import { Button, Card, CardBody, CardHeader, Input, Spinner } from "../components/ui.js";

function storedEmail(): string {
  try {
    return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? "";
  } catch {
    return "";
  }
}

export function LoginEmailLink(): JSX.Element {
  const { completeEmailLink } = useAuth();
  const { features, isLoading } = useFeatures();
  const firebase = features.emailLink?.firebase ?? null;
  const firebaseKey = firebase?.apiKey ?? "";
  const enabled = Boolean(features.emailLink?.enabled && firebase);
  const [email, setEmail] = useState(storedEmail);
  const [status, setStatus] = useState<"working" | "need-email" | "error">(() =>
    storedEmail() ? "working" : "need-email",
  );
  const [error, setError] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState<LoginRejectReason | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  // Once a completion attempt finishes (reject or invalid link), do not restart
  // it. A new `firebase` object / features refetch used to call setStatus("working")
  // and hide the allowlist error after about a second.
  const terminalRef = useRef(false);

  const fail = (message: string, reason?: LoginRejectReason) => {
    terminalRef.current = true;
    persistLoginError(message);
    setRejectReason(reason);
    setStatus("error");
    setError(message);
  };

  useEffect(() => {
    if (isLoading || terminalRef.current) return;
    if (!enabled || !firebase) {
      fail("Email-link sign-in is not available on this deployment.");
      return;
    }
    const href = window.location.href;
    const auth = getFirebaseAuth(firebase);
    if (!isSignInWithEmailLink(auth, href)) {
      fail("This is not a valid sign-in link. Request a new one from the login page.");
      return;
    }
    const toUse = email.trim();
    if (!toUse) {
      setStatus("need-email");
      return;
    }
    let cancelled = false;
    setStatus("working");
    void (async () => {
      try {
        const cred = await signInWithEmailLink(auth, toUse, href);
        const idToken = await cred.user.getIdToken();
        try {
          window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
        } catch {
          /* ignore */
        }
        await completeEmailLink(idToken);
        await signOut(auth);
      } catch (err) {
        const described = describeEmailLinkCompleteError(err);
        persistLoginError(described.message);
        // Always keep an allowlist/inactive reject, even if Strict Mode cleaned
        // up this effect — a retry would consume the one-time link and replace
        // the real error with "invalid or expired".
        if (cancelled && !described.reason) return;
        fail(described.message, described.reason);
      }
    })();
    return () => {
      cancelled = true;
    };
    // completeEmailLink is stable (useCallback). `attempt` retriggers after the email prompt.
    // email is snapshotted at attempt time (no react-hooks plugin — same as #46).
  }, [isLoading, enabled, firebaseKey, attempt, completeEmailLink]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-bold text-brand">
            RR<span className="text-slate-400">\</span>ID — Sign in
          </h1>
        </CardHeader>
        <CardBody className="space-y-4">
          {status === "working" && !error ? <Spinner label="Completing sign-in…" /> : null}
          {status === "need-email" ? (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!email.trim()) return;
                try {
                  window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email.trim());
                } catch {
                  /* ignore */
                }
                setError(null);
                setAttempt((n) => n + 1);
              }}
            >
              <p className="text-sm text-slate-600">
                Confirm the email address this sign-in link was sent to (this browser does not
                remember it).
              </p>
              <Input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button className="w-full" type="submit">
                Continue
              </Button>
            </form>
          ) : null}
          {error ? (
            <div className="space-y-3">
              <div
                className="rounded-md border-2 border-red-300 bg-red-50 px-3 py-3 text-sm text-red-950"
                role="alert"
                aria-live="assertive"
              >
                <p className="font-semibold">{error}</p>
              </div>
              <a
                href={
                  rejectReason
                    ? unauthorizedLoginPath(rejectReason, email.trim() || undefined)
                    : "/login"
                }
                className="text-sm font-medium text-brand underline underline-offset-2"
              >
                Back to login
              </a>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
